<script lang="ts" setup>
import { computed, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useFetch } from '@/home/hooks/fetch';
import { useAsyncData } from '@/home/hooks/async-data';
import { useTitleForClient } from '@/home/hooks/browser';
import SidePanel, { type EngagementData } from './components/SidePanel.vue';
import { useI18n } from 'vue-i18n';

import Prism from 'virtual:prismjs';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  pageType: {
    type: String,
    default: 'detail',
  },
});

const { $fetch } = useFetch();
const router = useRouter();
const { setTitle } = useTitleForClient();
const { t } = useI18n();
const engagement = ref<EngagementData>();
const defaultTitle = props.pageType === 'preview' ? t('home.explore.journalism.previewTitle') : t('home.explore.journalism.title');
const contentEl = ref();

const generateTitle = (title: string) => `${title} - ${defaultTitle}`;

const renderHighlight = () => {
  nextTick(() => {
    Prism.highlightAllUnder(contentEl.value);
  });
};

const { data: detail } = useAsyncData(
  'explore:journalism:detail',
  () => $fetch(props.pageType === 'preview' ? '/information/preview' : '/information/view', { data: { id: props.id } }),
  {
    cache: false,
    watch: [() =>  props.id],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (serverContext) {
        if (handleSuccess && detail.value) {
          serverContext.title = generateTitle(detail.value.informationTitle);
          serverContext.description = detail.value.informationMsg;
          serverContext.keywords = detail.value.informationKeywords;
        }
        else {
          // 如果请求出错直接服务器重定向到 404 页面
          serverContext.redirect = '/404';
        }
      }
      else {
        if (handleSuccess && detail.value) {
          setTitle(generateTitle(detail.value.informationTitle));
          renderHighlight();
        }
        else {
          router.replace('/404');
        }
      }
    },
  },
);

const { data: engagementDetail } = useAsyncData(
  'explore:journalism:detail:engagement',
  () => $fetch<EngagementData>('/information/like/num', { data: { informationId: props.id } }),
  {
    cache: false,
    watch: [() =>  props.id],
    onAfterHandle({ handleSuccess }) {
      if (handleSuccess) {
        engagement.value = { ...engagementDetail.value };
      }
    },
  },
)

const labelList = computed(() => {
  return detail.value?.labelVOList || [];
});

const handleChange = (data: EngagementData) => {
  engagement.value = { ...data };
}
</script>

<template>
  <main class="page-explore-detail relative w-100% bg-hex-ffffff pb-44 pt-24">
    <div class="absolute top-0 z-0 h-280 w-100% bg-hex-f7f7f7"></div>
    <div class="grid grid-cols-[1fr_auto_1fr]">
      <SidePanel :id="props.id" @change="handleChange" />
      <div class="relative z-1 mx-auto max-w-710 min-h-1000 rounded-4 pt-0 filter-unset" v-if="detail">
        <img class="mb-40 max-h-417 w-full rounded-4 object-cover" :src="detail.coverUrl" alt="banner">
        <h1 class="break-anywhere text-center text-24 color-hex-292929 font-500 leading-40">
          {{ props.pageType === 'preview' ? $t('home.explore.journalism.preview') : '' }}{{ detail.informationTitle }}
        </h1>
        <div class="mt-40 text-center text-12 color-hex-8b8b8b font-400 leading-22">
          <span class="mr-8">{{ $t('home.explore.journalism.public', { date: detail.publishTime }) }}</span>
          <span>
            {{ $t('home.explore.journalism.view', { total: engagement?.informationViews }) }}
            ·
            {{ $t('home.explore.journalism.like', { total: engagement?.informationLikeCount }) }}
            ·
            {{ $t('home.explore.journalism.favorite', { total: engagement?.informationCollections }) }}
          </span>
        </div>

        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="detail.informationContent" class="detail-content line-numbers mt-40 break-anywhere text-justify color-hex-292929" ref="contentEl" :data-prismjs-copy="$t('common.prismjs.copy')" :data-prismjs-copy-error="$t('common.prismjs.copyError')" :data-prismjs-copy-success="$t('common.prismjs.copySuccess')">
        </div>

        <div class="mt-40 flex border-hex-f7f7f7" v-if="labelList.length">
          <span
            v-for="(item, index) in labelList"
            :key="index"
            class="mr-10 block h-22 w-auto cursor-pointer rounded-11 bg-hex-f7f7f7 px-8 py-3 text-center text-12 color-hex-8b8b8b font-400 leading-16"
          >
            #{{ item.labelName }}
          </span>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-explore-detail {
  .detail-content:deep() {
    h1 {
      font-size: 22px;
    }

    h2 {
      font-size: 20px;
    }

    h1,
    h2 {
      margin-top: 48px;
      font-weight: 500;
    }

    ol,
    ul,
    menu {
      margin-top: 1em;
      margin-bottom: 1em;
      padding-left: 40px;
    }

    ol {
      list-style: decimal;
    }

    ul {
      list-style: disc;
    }

    p,
    section {
      margin-top: 1em;
      margin-bottom: 1em;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }

    img {
      max-width: 100%;
    }

    table {
      td {
        border-color: gray;
        border-width: 1px;
      }
    }
  }
}
</style>
